import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, MessageCircle, User, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Message {
  id: string;
  user_id: string;
  sender_role: string;
  message: string;
  created_at: string;
}

interface ConciergeThreadProps {
  conciergeRequestId: string;
  isAdmin?: boolean;
}

export function ConciergeThread({ conciergeRequestId, isAdmin = false }: ConciergeThreadProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id || null);
    });
  }, []);

  // Fetch messages
  useEffect(() => {
    async function fetch() {
      setLoading(true);
      const { data, error } = await supabase
        .from("concierge_messages")
        .select("*")
        .eq("concierge_request_id", conciergeRequestId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error(error);
      } else {
        setMessages((data as Message[]) || []);
      }
      setLoading(false);
    }
    fetch();
  }, [conciergeRequestId]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel(`concierge-messages-${conciergeRequestId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "concierge_messages",
          filter: `concierge_request_id=eq.${conciergeRequestId}`,
        },
        (payload) => {
          setMessages((prev) => {
            if (prev.some((m) => m.id === (payload.new as Message).id)) return prev;
            return [...prev, payload.new as Message];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conciergeRequestId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed || !currentUserId) return;
    if (trimmed.length > 2000) {
      toast.error("Message is too long (max 2000 characters)");
      return;
    }

    setSending(true);
    const { error } = await supabase.from("concierge_messages").insert({
      concierge_request_id: conciergeRequestId,
      user_id: currentUserId,
      sender_role: isAdmin ? "admin" : "user",
      message: trimmed,
    });

    if (error) {
      console.error(error);
      toast.error("Failed to send message");
    } else {
      setNewMessage("");
      // Send email notification when admin replies
      if (isAdmin) {
        supabase.functions.invoke("notify-admin-reply", {
          body: {
            conciergeRequestId: conciergeRequestId,
            messagePreview: trimmed,
          },
        }).catch((err) => console.error("Email notification failed:", err));
      }
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " · " +
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <section>
      {!isAdmin && (
        <div className="mb-4 flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-primary" />
          <h2 className="font-serif text-xl font-semibold text-foreground">Conversation</h2>
        </div>
      )}

      <div className={`${isAdmin ? "" : "rounded-xl border border-border bg-card shadow-sm"} overflow-hidden`}>
        {/* Messages list */}
        <div className="max-h-80 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="py-6 text-center">
              <MessageCircle className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">
                No messages yet. Send a follow-up question or additional details.
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isAdmin = msg.sender_role === "admin";
              const isOwn = msg.user_id === currentUserId;
              return (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${isOwn && !isAdmin ? "flex-row-reverse" : ""}`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      isAdmin
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isAdmin ? <Shield className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                  </div>
                  <div
                    className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                      isAdmin
                        ? "bg-primary/10 border border-primary/20"
                        : isOwn
                        ? "bg-muted"
                        : "bg-muted"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        {isAdmin ? "MINZO Team" : "You"}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                      {msg.message}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-border bg-muted/30 p-4">
          <div className="flex gap-2">
            <Textarea
              placeholder="Type a follow-up message…"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[44px] max-h-32 resize-none bg-background"
              rows={1}
            />
            <Button
              size="icon"
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </div>
    </section>
  );
}
